import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { PageHeader, Loading, ErrorBox, Badge } from '../components/ui';

export function CartReviewPage() {
  const qc = useQueryClient();
  const queue = useQuery({ queryKey: ['admin-review'], queryFn: () => adminApi.reviewQueue() });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = (queue.data as any)?.items ?? [];
  const activeId = selectedId ?? items[0]?.id;
  const detail = useQuery({ queryKey: ['admin-review', activeId], queryFn: () => adminApi.reviewItem(activeId!), enabled: !!activeId });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-review'] });
    qc.invalidateQueries({ queryKey: ['admin-nav-counts'] });
  };
  const approve = useMutation({ mutationFn: () => adminApi.reviewApprove(activeId!), onSuccess: invalidate });
  const reject = useMutation({ mutationFn: (reason: string) => adminApi.reviewReject(activeId!, reason), onSuccess: invalidate });
  const reupload = useMutation({ mutationFn: () => adminApi.reviewRequestReupload(activeId!), onSuccess: invalidate });

  if (queue.isLoading) return <Loading />;
  if (queue.error) return <ErrorBox error={queue.error} />;

  const d = detail.data as any;

  return (
    <div>
      <PageHeader title="Cart review" subtitle="Screenshot and flagged carts waiting for a human check" />
      <div className="grid grid-cols-[280px_1fr_320px] gap-4">
        <div className="card p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">Queue</span>
            <Badge tone="warn">{items.length} left</Badge>
          </div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {items.map((it: any) => (
              <button
                key={it.id}
                onClick={() => setSelectedId(it.id)}
                className="w-full text-left px-2 py-2 rounded-lg text-xs"
                style={it.id === activeId ? { background: 'var(--warn-bg-soft)' } : {}}
              >
                <div className="font-semibold" style={{ color: 'var(--text-heading)' }}>{it.customer ?? it.customerName}</div>
                <div style={{ color: 'var(--text-faint)' }}>{it.store} · {it.source}</div>
                <Badge tone={it.confidence < 60 ? 'danger' : it.confidence < 80 ? 'warn' : 'success'}>{it.confidenceLabel ?? it.confidence}</Badge>
              </button>
            ))}
          </div>
        </div>

        <div className="card p-4 flex flex-col items-center justify-center">
          {d?.screenshotUrl ? (
            <img src={d.screenshotUrl} alt="screenshot" className="max-h-[70vh] rounded-xl" />
          ) : (
            <div className="text-sm" style={{ color: 'var(--text-faint)' }}>{d ? 'No image for this cart' : 'Select a cart'}</div>
          )}
        </div>

        <div className="card p-4">
          {d ? (
            <>
              <div className="font-semibold">{d.customer}</div>
              <div className="text-xs mb-3" style={{ color: 'var(--text-faint)' }}>{d.phone} · {d.ref}</div>
              <div className="space-y-2 text-sm mb-4">
                {(d.lines ?? []).map((l: any) => (
                  <div key={l.id ?? l.name} className="flex justify-between">
                    <span>{l.qty}× {l.name}</span>
                    <span>{l.price}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs mb-1" style={{ color: 'var(--text-faint)' }}>Platform total</div>
              <div className="font-display text-xl mb-4">{d.platformTotal}</div>
              <div className="flex flex-col gap-2">
                <button className="btn-accent" disabled={approve.isPending} onClick={() => approve.mutate()}>Approve &amp; send quote</button>
                <button className="btn-outline" disabled={reupload.isPending} onClick={() => reupload.mutate()}>Request clearer screenshot</button>
                <button className="btn-outline" style={{ color: 'var(--danger)' }} disabled={reject.isPending} onClick={() => reject.mutate('Unreadable')}>Reject</button>
              </div>
            </>
          ) : (
            <div className="text-sm" style={{ color: 'var(--text-faint)' }}>Nothing selected</div>
          )}
        </div>
      </div>
    </div>
  );
}
